import { useMap, useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { allMsgsKey, ss, tenSeconds } from "consts";
import { MessageModel, MessagePreviewModel } from "models";
import {
  AttBaseProviderType,
  PermissionAttProviderType,
  useAttendDetail,
} from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as AttendanceService from "services/attendance";
import { aytyFormatError } from "utils";

type UndefinedMap = Map<number, MessageModel> | undefined;

type MessageContextType = {
  loading: boolean;
  messages?: MessageModel[];
  previews?: Map<number, MessagePreviewModel>;
  getMessages: (isReload?: boolean) => Promise<void>;
  addMessage: (key: number, value: MessageModel) => Promise<UndefinedMap>;
  editMessage: (key: number, value: MessageModel) => Promise<UndefinedMap>;
  removeMessage: (key: number) => Promise<UndefinedMap>;
};

const MessageContext = createContext({} as MessageContextType);

const Provider = ({ children, all }: AttBaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [previews, setPreviews] = useState<Map<number, MessagePreviewModel>>();
  const [list, { set, add, edit, remove }] = useMap<number, MessageModel>();
  const { pIdOmni } = useAttendDetail();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const messages = useMemo(() => {
    if (list) return Array.from(list.values());
  }, [list]);

  useEffect(() => {
    if (pIdOmni) setLoading(true);
  }, [pIdOmni]);

  const getMessages = useCallback(
    async (isReload?: boolean) => {
      if (isReload ?? loading)
        try {
          const result = AttendanceService.getMessagesIdSync({
            pIdOmni,
            pWasGet: true,
          });

          const current: MessageModel[] = all[pIdOmni];
          const msgs = new Map<number, MessageModel>(
            current?.map((c) => [c.IdOmniMessage, c])
          );

          if (result.idReturnAPI > 0) {
            const previews = new Map<number, MessagePreviewModel>();
            const list = new Array<string>();

            result.omniMessageListOnLine.forEach((preview) => {
              if (!msgs.has(preview.IdOmniMessage))
                list.push(preview.IdOmniMessage.toString());

              previews.set(preview.IdOmniMessage, preview);
            });

            setPreviews(previews);

            if (list.length > 0) {
              setLoading(true);
              await AttendanceService.getMessages({
                pIdOmni,
                pIdOmniMessage: list,
              }).then(({ data }) => {
                data.omniMessageListOnLine.forEach((msg) =>
                  msgs.set(msg.IdOmniMessage, msg)
                );
              });
            }
          } else warning({ description: t(aytyFormatError(result)) });

          set(msgs);
          all[pIdOmni] = Array.from(msgs.values());
          ss.setItem(allMsgsKey, JSON.stringify(all));
        } catch (e: unknown) {
          if (e instanceof Error) errorsResolver.defaultSyncError(e);
          else console.log("GetMessages", e);
        } finally {
          setLoading(false);
        }
    },
    [all, pIdOmni, loading, errorsResolver, set, warning, t]
  );

  useEffect(() => {
    const interval = setInterval(() => getMessages(true), tenSeconds);
    return () => clearInterval(interval);
  }, [getMessages]);

  return (
    <MessageContext.Provider
      value={{
        loading,
        messages,
        previews,
        getMessages,
        addMessage: add,
        editMessage: edit,
        removeMessage: remove,
      }}
    >
      {children}
    </MessageContext.Provider>
  );
};

export const MessageProvider = ({
  hasPermission = true,
  ...args
}: PermissionAttProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useMessage = () => useContext(MessageContext);
